import { InputLabel, MenuItem, SelectChangeEvent } from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { ItemVM } from 'shell/view-models/item';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { Service } from 'shell/models/service';
import { Operation } from 'shell/view-models/operation';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';

interface ListServiceProps {
  disabled?: boolean;
  value: string;
  onChange: (event: SelectChangeEvent<string>) => void;
  label?: string;
  errorSelect?: boolean;
  fgCashManagement?: string;
}

const ListService = ({
  disabled,
  onChange,
  value,
  label = 'Servizio',
  errorSelect = false,
  fgCashManagement,
}: ListServiceProps): React.ReactElement => {
  const {
    data,
    error,
    get: getListServices,
  } = useHttpFetch<HttpResponseList<Service>>(`${__MANAGEMENT_API_URL__}/servizi`, {
    data: {
      payload: {
        list: [],
      },
    },
  });

  const services: Array<ItemVM> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }

    return data?.payload.list.map((service) => ({
      id: service.key.codiceServizio,
      value: service.descrizione,
    }));
  }, [data, error]);

  useEffect(() => {
    if (fgCashManagement) {
      getListServices(`?filters=gestioneCassa:${fgCashManagement}:${Operation.EQUALS}`);
    } else {
      getListServices();
    }
  }, []);

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={errorSelect} id={`service-${label}`}>
        {label}
      </InputLabel>
      <PmiSelect
        labelId={`service-${label}`}
        id="service"
        aria-label={label}
        name="service"
        value={value}
        label={label}
        onChange={onChange}
        disabled={disabled}
        error={errorSelect}
      >
        <MenuItem value="ALL">
          <em>Tutti i servizi</em>
        </MenuItem>
        {services
          .sort((a, b) => a.value?.localeCompare(b.value))
          .map((service: ItemVM) => (
            <MenuItem key={service.id} value={service.id}>
              {service.value}
            </MenuItem>
          ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ListService.displayName = 'List Service component';

const MemoizedListService = memo(ListService);

export default MemoizedListService as typeof ListService;
